import { useTranslation } from "react-i18next"

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Table, TableBody, TableCell, TableRow } from "@/components/ui/table"
import type {
  AdminAnalyticsOverlap,
  AdminAnalyticsOverlapBucket,
  AdminAnalyticsOverlapBucketLabel,
} from "@/types/admin-analytics"

/**
 * OKS-66 — Cross-brand creator overlap card.
 *
 * Per UX Spec §2 — large scalar `"{N} creators in 2+ brand crews"` +
 * 3-row mini distribution table (1 / 2 / 3+ crews). Headline carries
 * a sr-only suffix `"… of {total} total creators"` so AT reads the
 * scalar with full denominator context.
 *
 * Share rendering (TRD K7 contract):
 *   - `share === null`   → `—%`  (no denominator; `total_creators === 0`)
 *   - `share === 0`      → `0.0%` (denominator exists, bucket is empty)
 *   - `0 < share <= 1`   → `{(share * 100).toFixed(1)}%`
 *
 * Bucket order is fixed (one / two / three_plus) — see TRD K7.
 */
export interface AnalyticsOverlapCardProps {
  overlap: AdminAnalyticsOverlap
}

export function AnalyticsOverlapCard({ overlap }: AnalyticsOverlapCardProps) {
  const { t } = useTranslation()

  // Headline uses i18next pluralization — `_one` vs `_other` keys.
  const headline = t("admin.analytics.overlap.headline", {
    count: overlap.multi_brand_creators,
  })
  const ofTotalSuffix = t("admin.analytics.overlap.of_total_creators", {
    count: overlap.total_creators,
  })
  const footerCaption = t("admin.analytics.overlap.footer_caption", {
    count: overlap.total_creators,
  })

  return (
    <Card data-testid="admin-analytics-overlap-card">
      <CardHeader>
        <CardTitle className="text-base font-semibold">
          {t("admin.analytics.overlap.title")}
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <p
          className="text-foreground text-3xl font-semibold"
          data-testid="admin-analytics-overlap-headline"
        >
          {headline}
          <span className="sr-only"> {ofTotalSuffix}</span>
        </p>
        <Table data-testid="admin-analytics-overlap-distribution">
          <TableBody>
            {overlap.buckets.map((bucket) => (
              <BucketRow key={bucket.label} bucket={bucket} />
            ))}
          </TableBody>
        </Table>
        <p className="text-muted-foreground text-xs">{footerCaption}</p>
      </CardContent>
    </Card>
  )
}

/**
 * Renders one distribution row. The bucket label-to-i18n-key mapping
 * uses explicit `t()` calls per case (not a dict lookup) so the
 * i18next health checker can trace each key statically.
 */
function BucketRow({ bucket }: { bucket: AdminAnalyticsOverlapBucket }) {
  const { t } = useTranslation()
  const label = bucketLabelText(t, bucket.label)
  const shareText = formatShare(
    bucket.share,
    t("admin.analytics.overlap.share_unavailable"),
  )

  return (
    <TableRow>
      <TableCell className="text-muted-foreground py-2 text-sm">
        {label}
      </TableCell>
      <TableCell className="text-foreground py-2 text-right text-sm tabular-nums">
        {bucket.count.toLocaleString()}
        {" · "}
        <span data-testid={`admin-analytics-overlap-share-${bucket.label}`}>
          {shareText}
        </span>
      </TableCell>
    </TableRow>
  )
}

function bucketLabelText(
  t: ReturnType<typeof useTranslation>["t"],
  label: AdminAnalyticsOverlapBucketLabel,
): string {
  // Three explicit `t()` calls — one per bucket. Avoids the i18next
  // `_one` plural-form collision a `bucket_one` key would trigger.
  switch (label) {
    case "one":
      return t("admin.analytics.overlap.crews_1")
    case "two":
      return t("admin.analytics.overlap.crews_2")
    case "three_plus":
      return t("admin.analytics.overlap.crews_3plus")
  }
}

/**
 * Share renderer. Distinct `null` ("no data") vs `0` ("0 / N, N > 0")
 * semantic per TRD K7.
 */
function formatShare(share: number | null, unavailable: string): string {
  if (share === null) return unavailable
  return `${(share * 100).toFixed(1)}%`
}
